// #region Imports
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import CommentIcon from "@mui/icons-material/Comment";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import { MaskedTeam } from "work/_shared/masked-fields";
import { Content, Header, ProposalTab, TabContent, TabsContainer } from "work/_views/work-page-styled-components";
import { useGetProposalByIdQuery } from "work/proposals/_api/proposal-api";
import { getProposalById, isProposalLoadIssue } from "work/proposals/_store/proposals-redux-slice";
import ArchivedBadge from "work/proposals/_views/archive/archived-badge";
import ConflictsTab from "work/proposals/_views/archive/tabs/archived-proposal-conflicts-tab";
import DetailsTab from "work/proposals/_views/archive/tabs/archived-proposal-details-tab";
import DiscountTab from "work/proposals/_views/archive/tabs/archived-proposal-discount-tab";
import FeeScheduleTab from "work/proposals/_views/archive/tabs/archived-proposal-fee-schedule-tab";
import PoliciesTab from "work/proposals/_views/archive/tabs/archived-proposal-policies-tab";
import TeamTab from "work/proposals/_views/archive/tabs/archived-proposal-team-tab";
import { PageTab } from "work/proposals/_views/drafts/draft-proposal-page-tabs";
import Comments from "work/proposals/comments/_view/comments";
import {
  getCommentThreadsByProposal,
  getErrorLoadingCommentThreadsByProposal,
  getIsLoadingCommentThreadsByProposal,
  populateCommentThreadsByProposal,
} from "work/proposals/comments/threads/_store/comment-thread-redux-slice";
import Proposal from "work/proposals/proposal";
import ProposalField, {
  HumanReadableProposalFieldName,
  ProposalFieldCategory,
} from "work/proposals/proposal-fields/proposal-field";
// #endregion

// #region Styled Components
const PageContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  height: "100%",
}));
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexDirection: "row",
  justifyContent: "space-between",
  paddingRight: theme.spacing(3),
}));
const TitleContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
}));
const StickyPageHeader = styled("section")(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  position: "sticky",
  top: 0,
  zIndex: 20,
  "&::before": {
    backgroundColor: theme.palette.background.default,
    content: '""',
    height: theme.spacing(1),
    left: 0,
    pointerEvents: "none",
    position: "absolute",
    right: 0,
    top: theme.spacing(-1),
  },
}));
const Main = styled("main", {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "showCommentDrawer",
})<{
  open?: boolean;
  showCommentDrawer?: boolean;
}>(({ theme, showCommentDrawer }) => ({
  display: "flex",
  height: "100%",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  padding: theme.spacing(3),
  paddingTop: theme.spacing(1),
  paddingRight: showCommentDrawer ? 0 : theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginRight: showCommentDrawer ? "-400px" : 0,
  /**
   * This is necessary to enable the selection of content. In the DOM, the stacking order is determined
   * by the order of appearance. Following this rule, elements appearing later in the markup will overlay
   * those that appear earlier. Since the Drawer comes after the Main content, this adjustment ensures
   * proper interaction with the underlying content.
   */
  position: "relative",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginRight: 0,
      },
    },
  ],
}));
const ButtonContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
}));
const MessageButton = styled(IconButton)(() => ({
  height: 40,
  width: 40,
}));
const MessageButtonLabel = styled(Typography)(() => ({
  fontSize: "0.6em",
}));
const CommentsPanelContainer = styled("div")(() => ({
  flex: 1,
  height: "100%",
}));
const Drawer = styled(MuiDrawer)(() => ({
  width: "400px",
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    marginTop: "64px",
    width: "400px",
    boxSizing: "border-box",
  },
}));
const CommentsPanel = styled(Drawer, { shouldForwardProp: (prop) => prop !== "closeAfterTransition" })(() => ({}));
const CommentsPanelContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  padding: theme.spacing(2),
  width: "400px",
}));
const TitleBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  flexDirection: "row",
  paddingBottom: theme.spacing(1),
}));
const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));
const ArchivedHeader = styled(Header)(() => ({
  paddingLeft: 0,
}));
const ArchivedContent = styled(Content, {
  shouldForwardProp: (prop) => prop !== "isFeeScheduleTab",
})<{ isFeeScheduleTab?: boolean }>(({ theme, isFeeScheduleTab }) => ({
  ...(isFeeScheduleTab && {
    marginLeft: theme.spacing(-3),
    width: `calc(100% + ${theme.spacing(3)})`,
  }),
}));
// #endregion

export default function ArchivedProposalPage() {
  // #region Refs
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const { id: idParam, pageTab: pageTabParam } = useParams();
  const navigate = useNavigate();

  // Proposal id is required
  if (!idParam) {
    throw new Response("Not Found", { status: 404 });
  }

  // #region State
  const [currentCommentField, setCurrentCommentField] = useState<ProposalField | undefined>();
  const [commentsPanelIsOpen, setCommentsPanelIsOpen] = useState<boolean>(true);
  const [commentsPanelTitle, setCommentsPanelTitle] = useState<string | undefined>();
  // #endregion

  // #region Globals
  const proposalId = useMemo(() => (idParam ? new Guid(idParam) : undefined), [idParam]);
  const activeTab = useMemo(() => {
    const tab = pageTabParam as PageTab | undefined;
    return tab ?? PageTab.Details;
  }, [pageTabParam]);

  // #region Permissions Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);

  // #region Proposal Queries
  const proposalJson = useSelector(getProposalById(proposalId));
  const proposal = useMemo(() => {
    if (!proposalJson || isProposalLoadIssue(proposalJson) || !session.user) return null;
    return Proposal.fromJSON({
      json: proposalJson,
      currentUser: session.user,
    });
  }, [proposalJson, session.user]);

  const proposalQuery = useGetProposalByIdQuery(
    !proposalId || proposalId.isEmpty ? skipToken : { proposalId: proposalId?.value ?? Guid.EMPTY.value, session },
  );

  // #region Comments Queries
  const commentThreads = useSelector(getCommentThreadsByProposal(proposal));
  const isLoadingCommentThreads = useSelector(getIsLoadingCommentThreadsByProposal(proposal));
  const wasErrorLoadingCommentThreads = useSelector(getErrorLoadingCommentThreadsByProposal(proposal));
  const isLoading = proposalQuery.isLoading || isLoadingCommentThreads;

  // #region Lifecycle
  useEffect(() => {
    // Proposal invalid/not found
    const shouldShowNotFound =
      Boolean(proposalId) && !proposalId?.isEmpty && proposal === null && !proposalQuery.isLoading;

    if (shouldShowNotFound && idParam) {
      enqueueSnackbar("Proposal not found", { variant: "warning" });
      navigate("/proposals/archived", { replace: true });
    }
  }, [activeTab, idParam, navigate, proposal, proposalId, proposalQuery.isLoading]);

  // Permissions bootstrap
  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);
  // #endregion

  // #region Functions
  function handleOpenCommentsPanel(field: ProposalField, commentsPanelTitle?: string) {
    setCommentsPanelIsOpen((prev) => (field === currentCommentField ? !prev : true));
    setCurrentCommentField(field);
    setCommentsPanelTitle(commentsPanelTitle);
  }

  // Comment threads bootstrap
  useEffect(() => {
    if (
      !proposal ||
      proposal.supersededById ||
      isLoadingCommentThreads ||
      wasErrorLoadingCommentThreads ||
      commentThreads
    )
      return;
    dispatch(populateCommentThreadsByProposal({ proposal, session }));
  }, [proposal, isLoadingCommentThreads, wasErrorLoadingCommentThreads, commentThreads, dispatch, session]);

  // Account view switching
  useEffect(() => {
    if (!proposal) return;
    if (
      MaskedTeam.isPresentNotMasked(proposal.vendorTeam) &&
      MaskedTeam.isPresentNotMasked(proposal.clientLeader) &&
      session.context?.viewingAsVendor &&
      proposal.clientLeader?.userId.isEqualTo(session.user?.id)
    ) {
      session.setAccountViewType(AccountType.Client);
    } else if (
      MaskedTeam.isPresentNotMasked(proposal.vendorTeam) &&
      (proposal.vendorTeam?.leader?.userId.isEqualTo(session.user?.id) ||
        proposal.vendorTeam?.members?.some((member) => member.userId.isEqualTo(session.user?.id)))
    ) {
      session.setAccountViewType(AccountType.Vendor);
    }
  }, [proposal, proposal?.id?.value, session]);

  function handleTabChange(_event: ChangeEvent<object> | null, newTab: PageTab) {
    setCommentsPanelIsOpen(false);
    setCurrentCommentField(undefined);
    setCommentsPanelTitle(undefined);
    navigate(`/proposals/archived/view/${proposalId?.value}/${newTab}`);
  }

  const handleCloseCommentsPanel = () => {
    setCommentsPanelIsOpen(false);
  };

  function getCommentsPanelTitle(): string {
    if (commentsPanelTitle) return `${commentsPanelTitle} Comments`;
    return `${HumanReadableProposalFieldName[currentCommentField?.name ?? ProposalField.General.name]} Comments`;
  }

  function renderComments() {
    if (proposal?.id && commentThreads) {
      return (
        <Comments
          proposal={proposal}
          field={currentCommentField ?? ProposalField.General}
          commentThreads={commentThreads}
          readOnly
          showMarkAllRead={false}
        />
      );
    }
  }

  // #region Render
  const handleNavigateBack = useCallback(() => {
    navigate("/proposals/archived", { replace: true });
  }, [navigate]);
  const isFeeScheduleTab = activeTab === PageTab.FeeSchedule;

  if (!sessionPermissions || isLoading)
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  if (!sessionPermissions.includesAccessToProposals) return <NotAuthorized />;

  return (
    <PageContainer>
      <Main open={commentsPanelIsOpen} showCommentDrawer={!!proposal}>
        <StickyPageHeader>
          <HeaderContainer>
            <TitleContainer>
              <Chip
                icon={<ArrowBackIcon />}
                size="medium"
                label="Back"
                onClick={handleNavigateBack}
                color="primary"
                sx={{ mr: 2 }}
              />
              <Typography variant="h4">
                <span style={{ color: "gray", marginRight: "8px" }}>Proposals &gt;</span>
                Archived Proposal
              </Typography>
            </TitleContainer>
            <ButtonContainer>
              <MessageButton
                size="medium"
                color="primary"
                disabled={!proposal?.id || !proposal || !commentThreads}
                onClick={() => handleOpenCommentsPanel(ProposalField.General)}
              >
                <Badge
                  variant="standard"
                  color="secondary"
                  overlap="rectangular"
                  invisible={!commentThreads?.some((thread) => thread.field.isEqualTo(ProposalField.General))}
                >
                  <CommentIcon fontSize="medium" />
                </Badge>
              </MessageButton>
              <MessageButtonLabel
                variant="button"
                color={!proposal?.id || !proposal || !commentThreads ? "darkgray" : "primary"}
              >
                Comments
              </MessageButtonLabel>
            </ButtonContainer>
          </HeaderContainer>
          <ArchivedHeader>
            <TabsContainer
              variant="scrollable"
              scrollButtons="auto"
              indicatorColor="primary"
              textColor="primary"
              value={activeTab}
              onChange={handleTabChange}
            >
              <ProposalTab value={PageTab.Details} label={<ArchivedBadge category={ProposalFieldCategory.Details} />} />
              <ProposalTab value={PageTab.Team} label={<ArchivedBadge category={ProposalFieldCategory.Team} />} />
              <ProposalTab
                value={PageTab.FeeSchedule}
                label={<ArchivedBadge category={ProposalFieldCategory.FeeSchedule} />}
              />
              <ProposalTab
                value={PageTab.Conflicts}
                label={<ArchivedBadge category={ProposalFieldCategory.Conflicts} />}
              />
              <ProposalTab
                value={PageTab.Policies}
                label={<ArchivedBadge category={ProposalFieldCategory.Policies} />}
              />
              <ProposalTab
                value={PageTab.Discount}
                label={<ArchivedBadge category={ProposalFieldCategory.Discount} />}
              />
            </TabsContainer>
          </ArchivedHeader>
        </StickyPageHeader>
        <>
          <ArchivedContent isFeeScheduleTab={isFeeScheduleTab}>
            <TabContent>
              {proposal && (
                <>
                  {activeTab === PageTab.Details && (
                    <DetailsTab
                      commentThreads={commentThreads ?? []}
                      proposal={proposal}
                      onCommentsClicked={handleOpenCommentsPanel}
                    />
                  )}
                  {activeTab === PageTab.Team && (
                    <TeamTab
                      entityId={session.user!.legalEntityId}
                      proposal={proposal}
                      commentThreads={commentThreads ?? []}
                      onCommentsClicked={handleOpenCommentsPanel}
                    />
                  )}
                  {activeTab === PageTab.FeeSchedule && (
                    <FeeScheduleTab
                      proposal={proposal}
                      commentThreads={commentThreads}
                      onCommentsClicked={handleOpenCommentsPanel}
                    />
                  )}
                  {activeTab === PageTab.Conflicts && (
                    <ConflictsTab
                      proposal={proposal}
                      commentThreads={commentThreads}
                      onCommentsClicked={handleOpenCommentsPanel}
                    />
                  )}
                  {activeTab === PageTab.Policies && (
                    <PoliciesTab
                      proposal={proposal}
                      commentThreads={commentThreads}
                      onCommentsClicked={handleOpenCommentsPanel}
                    />
                  )}
                  {activeTab === PageTab.Discount && (
                    <DiscountTab
                      proposal={proposal}
                      commentThreads={commentThreads}
                      onCommentsClicked={handleOpenCommentsPanel}
                    />
                  )}
                </>
              )}
            </TabContent>
          </ArchivedContent>
        </>
      </Main>
      {proposal && commentThreads && (
        <CommentsPanel open={commentsPanelIsOpen} anchor="right" variant="persistent" closeAfterTransition={false}>
          <CommentsPanelContainer>
            <CommentsPanelContent>
              <TitleBar>
                <Typography variant="h5">{getCommentsPanelTitle()}</Typography>
                <Tooltip title="Minimize comments panel">
                  <IconButton size="medium" onClick={handleCloseCommentsPanel}>
                    <KeyboardTabIcon fontSize="large" />
                  </IconButton>
                </Tooltip>
              </TitleBar>
              {renderComments()}
            </CommentsPanelContent>
          </CommentsPanelContainer>
        </CommentsPanel>
      )}
    </PageContainer>
  );
}
